import * as React from 'react';
import { compose } from 'redux';

import SignupByOtpTip from '../../SignupByOtpTip';
import Logo from '../../Logo';
import Button from '../../Button';
import SignupTermsOfService from '../../SignupTermsOfService';
import SignupInputAuthKey from '../../SignupInputAuthKey';
import { constants } from '@shopee-user/spx-customization-logic';
import { Form } from '@shopee/use-form-hook';
import { LoginLink } from '@shopee-user/shared-customization-ui';

import { Feature } from '@shopee-user/feature-toggle';
import { isFeatureEnabled } from '@shopee-user/business-customization-logic';
import { useResponsiveLayout, Layout } from '@shopee-user/layout-responsive';
import { useTranslation, isWebBridgeSupported } from '@shopee-user/platform';
import {
  withUserWebView,
  WithUserWebViewProps,
} from '@shopee-user/with-web-view';
import { useSignupByOtpOneCredentialFlowEvent } from '@tob/pages/signup-by-otp-one-credential-optional/flow-event';
import { SignupByOtpOneCredentialOptionalRequireAuthKeyProps } from '@shopee-user/business-customization-types';

import styles from './style.scss';
import commonStyles from '../../common.styles.scss';

type Props = WithUserWebViewProps &
  SignupByOtpOneCredentialOptionalRequireAuthKeyProps;

function _Content({ webViewNavigateToUrl, ...props }: Props) {
  const isSpxOrangeBackgroundUiEnabled = isFeatureEnabled(
    Feature.SPXOrangeBackgroundUI,
    false
  );
  const { layout } = useResponsiveLayout();
  const {
    form,
    onSubmitForm,
    state: { isButtonSubmitDisabled },
  } = props;
  const I18n = useTranslation();

  const [isAgreementChecked, setIsAgreementChecked] = React.useState(false);
  const { trackEvent } = useSignupByOtpOneCredentialFlowEvent();

  React.useLayoutEffect(() => {
    if (isSpxOrangeBackgroundUiEnabled && layout === Layout.Mobile) {
      document.body?.classList.add(`${commonStyles.bodyBackground}`);
    }

    return () => {
      if (isSpxOrangeBackgroundUiEnabled && layout === Layout.Mobile) {
        document.body?.classList.remove(`${commonStyles.bodyBackground}`);
      }
    };
  }, [layout]);

  const SpxLogo = layout === Layout.Mobile && (
    <div className={styles.logoContainer}>
      <Logo className={styles.logo} />
    </div>
  );

  const MobileSignupByOtpTip = layout === Layout.Mobile && (
    <SignupByOtpTip
      onClick={() => trackEvent('onSignupSubmitExternalSignupFormClick')}
    />
  );

  const DesktopSignupByOtpTip = layout === Layout.Desktop && (
    <SignupByOtpTip
      onClick={() => trackEvent('onSignupSubmitExternalSignupFormClick')}
    />
  );

  const MobileLoginLink = layout === Layout.Mobile && <LoginLink />;

  return (
    <div className={styles.container}>
      {SpxLogo}
      {DesktopSignupByOtpTip}
      <div className={styles.formContainer}>
        {MobileSignupByOtpTip}
        <Form>
          <SignupInputAuthKey
            {...form.fields.authKey}
            wrapperClassName={styles.inputAuthKey}
            errors={form.fields.authKey.errors.map((key: string) =>
              I18n.t(key)
            )}
          />
          <Button
            className={styles.submitButton}
            text={I18n.t('label_auth_next')}
            disabled={isButtonSubmitDisabled || !isAgreementChecked}
            onClick={onSubmitForm}
          />
        </Form>
        <SignupTermsOfService
          onTermsOfServiceClick={event => {
            event.stopPropagation();
            const termsOfServiceLink = isWebBridgeSupported()
              ? constants.TERMS_OF_SERVICE_WEBVIEW_LINK
              : constants.TERMS_OF_SERVICE_LINK;
            webViewNavigateToUrl(termsOfServiceLink);
          }}
          onPrivacyPolicyClick={event => {
            event.stopPropagation();
            const privacyPolicyLink = isWebBridgeSupported()
              ? constants.PRIVACY_POLICY_WEBVIEW_LINK
              : constants.PRIVACY_POLICY_LINK;
            webViewNavigateToUrl(privacyPolicyLink);
          }}
          onCookiePolicyClick={
            isFeatureEnabled(Feature.LoginTermsOfService)
              ? () => webViewNavigateToUrl(constants.COOKIE_POLICY_LINK)
              : undefined
          }
          checked={isAgreementChecked}
          onCheckboxClick={(checked: boolean) => {
            setIsAgreementChecked(checked);
          }}
        />
        {MobileLoginLink}
      </div>
    </div>
  );
}

const Content = compose<React.ComponentType<{}>>(withUserWebView({}))(_Content);

export { Content };
